import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import type { NormalisedError } from '/index';
import type {
  GetApiB2bInviteByInviteTokenResponse,
  PostApiB2bApplicationByIdSetInviteAcceptedResponse,
} from '/api/types.gen';

type InviteByToken = GetApiB2bInviteByInviteTokenResponse['invite'];
type SaveInviteApplication = PostApiB2bApplicationByIdSetInviteAcceptedResponse['application'];

export interface InvitesState {
  newApplication: SaveInviteApplication;
  error: NormalisedError | null;
  newStatus: string | null;
  showNewStatusFeedback: boolean | null;
  sending: boolean;
  requestedInvite: InviteByToken | null;
  inviteLang: string | null;
  fetching?: boolean;
}

interface SendInviteStatusStartedAction extends Action {
  type: 'SEND_INVITE_STATUS_STARTED';
}

interface SendInviteStatusFulfilledAction extends Action {
  type: 'SEND_INVITE_STATUS_FULFILLED';
}

interface SendInviteStatusRejectedAction extends Action {
  type: 'SEND_INVITE_STATUS_REJECTED';
  error: NormaliseErrorInput;
}

interface ResendInviteStatusStartedAction extends Action {
  type: 'RESEND_INVITE_STATUS_STARTED';
}

interface ResendInviteStatusFulfilledAction extends Action {
  type: 'RESEND_INVITE_STATUS_FULFILLED';
}

interface ResendInviteStatusRejectedAction extends Action {
  type: 'RESEND_INVITE_STATUS_REJECTED';
  error: NormaliseErrorInput;
}

interface RemoveInviteStatusStartedAction extends Action {
  type: 'REMOVE_INVITE_STATUS_STARTED';
}

interface RemoveInviteStatusFulfilledAction extends Action {
  type: 'REMOVE_INVITE_STATUS_FULFILLED';
}

interface RemoveInviteStatusRejectedAction extends Action {
  type: 'REMOVE_INVITE_STATUS_REJECTED';
  error: NormaliseErrorInput;
}

interface SaveInviteStatusStartedAction extends Action {
  type: 'SAVE_INVITE_STATUS_STARTED';
}

interface SaveInviteStatusFulfilledAction extends Action {
  type: 'SAVE_INVITE_STATUS_FULFILLED';
  application: SaveInviteApplication;
  status: string;
}

interface SaveInviteStatusRejectedAction extends Action {
  type: 'SAVE_INVITE_STATUS_REJECTED';
  error: NormaliseErrorInput;
}

interface SetShowNewStatusFeedbackAction extends Action {
  type: 'SET_SHOW_NEW_STATUS_FEEDBACK';
  showFeedback: boolean;
}

interface GetInviteByTokenStartedAction extends Action {
  type: 'GET_INVITE_BY_TOKEN_STARTED';
}

interface GetInviteByTokenFulfilledAction extends Action {
  type: 'GET_INVITE_BY_TOKEN_FULFILLED';
  invite: GetApiB2bInviteByInviteTokenResponse;
}

interface GetInviteByTokenRejectedAction extends Action {
  type: 'GET_INVITE_BY_TOKEN_REJECTED';
  error: NormaliseErrorInput;
}

type InvitesAction =
  | SendInviteStatusStartedAction
  | SendInviteStatusFulfilledAction
  | SendInviteStatusRejectedAction
  | ResendInviteStatusStartedAction
  | ResendInviteStatusFulfilledAction
  | ResendInviteStatusRejectedAction
  | RemoveInviteStatusStartedAction
  | RemoveInviteStatusFulfilledAction
  | RemoveInviteStatusRejectedAction
  | SaveInviteStatusStartedAction
  | SaveInviteStatusFulfilledAction
  | SaveInviteStatusRejectedAction
  | SetShowNewStatusFeedbackAction
  | GetInviteByTokenStartedAction
  | GetInviteByTokenFulfilledAction
  | GetInviteByTokenRejectedAction;

const defaultState: InvitesState = {
  newApplication: null,
  error: null,
  newStatus: null,
  showNewStatusFeedback: null,
  sending: false,
  requestedInvite: null,
  inviteLang: null,
};

function reducer(state: InvitesState = defaultState, action: InvitesAction): InvitesState {
  switch (action.type) {
    case 'SEND_INVITE_STATUS_STARTED': {
      return {
        ...state,
        inviteLang: null,
        sending: true,
      };
    }
    case 'SEND_INVITE_STATUS_FULFILLED': {
      return {
        ...state,
        sending: false,
      };
    }
    case 'SEND_INVITE_STATUS_REJECTED': {
      const { error } = action;
      return {
        ...state,
        sending: false,
        error: normaliseError(error),
      };
    }
    case 'RESEND_INVITE_STATUS_STARTED': {
      return {
        ...state,
        sending: true,
      };
    }
    case 'RESEND_INVITE_STATUS_FULFILLED': {
      return {
        ...state,
        sending: false,
      };
    }
    case 'RESEND_INVITE_STATUS_REJECTED': {
      const { error } = action;
      return {
        ...state,
        sending: false,
        error: normaliseError(error),
      };
    }
    case 'REMOVE_INVITE_STATUS_STARTED': {
      return {
        ...state,
        sending: true,
      };
    }
    case 'REMOVE_INVITE_STATUS_FULFILLED': {
      return {
        ...state,
        sending: false,
      };
    }
    case 'REMOVE_INVITE_STATUS_REJECTED': {
      const { error } = action;
      return {
        ...state,
        sending: false,
        error: normaliseError(error),
      };
    }
    case 'SAVE_INVITE_STATUS_STARTED': {
      return {
        ...defaultState,
        sending: true,
      };
    }
    case 'SAVE_INVITE_STATUS_FULFILLED': {
      const { application, status } = action;
      return {
        ...state,
        newApplication: application,
        newStatus: status,
        showNewStatusFeedback: true,
        sending: false,
      };
    }
    case 'GET_INVITE_BY_TOKEN_REJECTED':
    case 'SAVE_INVITE_STATUS_REJECTED': {
      const { error } = action;
      return {
        ...defaultState,
        newApplication: null,
        newStatus: null,
        error: normaliseError(error),
        sending: false,
        fetching: false,
      };
    }
    case 'SET_SHOW_NEW_STATUS_FEEDBACK': {
      const { showFeedback } = action;
      return {
        ...state,
        showNewStatusFeedback: showFeedback,
      };
    }
    case 'GET_INVITE_BY_TOKEN_STARTED': {
      return {
        ...state,
        fetching: true,
      };
    }
    case 'GET_INVITE_BY_TOKEN_FULFILLED': {
      const { invite } = action;
      return {
        ...state,
        requestedInvite: invite.invite,
        inviteLang: invite.language,
        fetching: false,
      };
    }
    default:
      return state;
  }
}

export default reducer;
